/**
 * TajwidLeaderboard — classement amical sur les scores du mode exercice.
 *
 *   /cours/tajwid/leaderboard          → vue globale (≥ 3 sourates)
 *   /cours/tajwid/leaderboard?surah=N  → classement d'une sourate précise
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Trophy, Medal, Target, TrendingUp, Users, Loader2,
} from 'lucide-react';
import { GlassCard, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api, { quranApi } from '../services/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(iso));
  } catch { return iso.slice(0, 10); }
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Trophy className="w-5 h-5 text-amber-400" aria-label="1er" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" aria-label="2e" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-700" aria-label="3e" />;
  return <span className="font-mono text-sm text-text-muted">#{rank}</span>;
};

const PercentBar = ({ percent }) => (
  <div className="flex items-center gap-2 min-w-[140px]">
    <div className="flex-1 h-1.5 rounded-full bg-surface-elevated overflow-hidden">
      <div
        className="h-full bg-emerald-500"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
    <span className="font-mono text-xs text-text-secondary w-12 text-right">
      {(percent ?? 0).toFixed(1)}%
    </span>
  </div>
);

const LeaderboardRow = ({ rank, row, scope }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.22, delay: Math.min(rank * 0.02, 0.3) }}
    className="grid grid-cols-[48px_1fr_180px_auto] items-center gap-3 px-4 py-3 rounded-lg border border-border bg-surface-elevated/40 hover:border-primary/30 transition-colors"
    data-testid={`leaderboard-row-${rank}`}
  >
    <div className="flex items-center justify-center">
      <RankBadge rank={rank} />
    </div>
    <div>
      <div className="text-sm text-text-primary font-medium truncate">
        {row.userName || '—'}
      </div>
      <div className="text-[11px] text-text-muted">
        {scope === 'global'
          ? <>
              <Target className="w-3 h-3 inline mr-1" />
              {row.surahsPlayed} sourate{row.surahsPlayed > 1 ? 's' : ''} ·
              {' '}{row.globalCorrect}/{row.globalTotal} bonnes réponses
            </>
          : <>
              {row.correct}/{row.total} correct · maj. {fmtDate(row.updatedAt)}
            </>}
      </div>
    </div>
    <PercentBar percent={scope === 'global' ? row.globalPercent : row.percent} />
    <div className="text-[11px] text-text-muted hidden sm:block">
      {scope === 'global' ? fmtDate(row.lastUpdated) : null}
    </div>
  </motion.div>
);

export const TajwidLeaderboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const surahQ = params.get('surah');
  const surahNumber = surahQ ? parseInt(surahQ, 10) : null;
  const scope = surahNumber ? 'surah' : 'global';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surahs, setSurahs] = useState([]);

  useEffect(() => {
    quranApi.getSurahs().then(
      (r) => setSurahs(r.data || []),
      () => setSurahs([]),
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = surahNumber ? `?surahNumber=${surahNumber}&limit=50` : '?limit=50';
    api.get(`/tajwid/scores/leaderboard${qs}`)
      .then((r) => { if (!cancelled) setRows(r.data?.rows || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [surahNumber]);

  const currentSurah = useMemo(
    () => surahs.find((s) => s.surah_number === surahNumber),
    [surahs, surahNumber],
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="tajwid-leaderboard-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours/tajwid')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="leaderboard-back"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('tajwid_leaderboard.back')}
      </Button>

      <SectionHeader
        overline={t('tajwid_leaderboard.overline')}
        title={
          scope === 'surah' && currentSurah
            ? `${t('tajwid_leaderboard.title_surah')} — ${currentSurah.name_french || currentSurah.name_arabic}`
            : t('tajwid_leaderboard.title_global')
        }
        subtitle={scope === 'global'
          ? t('tajwid_leaderboard.subtitle_global')
          : t('tajwid_leaderboard.subtitle_surah')}
      />

      {/* Scope switcher + surah picker */}
      <div className="mt-6 mb-6 flex items-center gap-3 flex-wrap" data-testid="leaderboard-controls">
        <div className="flex items-center gap-1 p-1 rounded-md bg-surface-elevated/60 border border-border">
          <button
            type="button"
            onClick={() => setParams({})}
            aria-pressed={scope === 'global'}
            className={`px-3 py-1 rounded text-xs transition-colors ${
              scope === 'global' ? 'bg-primary/20 text-primary' : 'text-text-muted hover:text-text-primary'
            }`}
            data-testid="leaderboard-tab-global"
          >
            <Users className="w-3 h-3 inline mr-1" />
            {t('tajwid_leaderboard.scope_global')}
          </button>
          <button
            type="button"
            onClick={() => setParams({ surah: String(surahNumber || 1) })}
            aria-pressed={scope === 'surah'}
            className={`px-3 py-1 rounded text-xs transition-colors ${
              scope === 'surah' ? 'bg-primary/20 text-primary' : 'text-text-muted hover:text-text-primary'
            }`}
            data-testid="leaderboard-tab-surah"
          >
            <TrendingUp className="w-3 h-3 inline mr-1" />
            {t('tajwid_leaderboard.scope_surah')}
          </button>
        </div>
        {scope === 'surah' && (
          <select
            value={surahNumber || 1}
            onChange={(e) => setParams({ surah: e.target.value })}
            className="text-xs bg-surface-elevated border border-border rounded-md px-2 py-1 text-text-primary focus:border-primary/60 focus:outline-none max-w-[220px]"
            data-testid="leaderboard-surah-select"
          >
            {surahs.map((s) => (
              <option key={s.surah_number} value={s.surah_number}>
                {s.surah_number}. {s.name_french || s.name_arabic}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="leaderboard-loading">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <GlassCard className="text-center py-10" data-testid="leaderboard-empty">
          <Trophy className="w-10 h-10 text-text-muted mx-auto mb-3 opacity-50" />
          <p className="text-sm text-text-primary mb-1">{t('tajwid_leaderboard.empty_title')}</p>
          <p className="text-xs text-text-muted mb-4">
            {scope === 'global'
              ? t('tajwid_leaderboard.empty_subtitle_global')
              : t('tajwid_leaderboard.empty_subtitle_surah')}
          </p>
          <Link to={surahNumber ? `/quran/${surahNumber}` : '/quran'}>
            <Button size="sm" data-testid="leaderboard-empty-cta">
              <Target className="w-4 h-4 mr-2" />
              {t('tajwid_leaderboard.start_cta')}
            </Button>
          </Link>
        </GlassCard>
      ) : (
        <div className="space-y-2" data-testid="leaderboard-rows">
          {rows.map((row, i) => (
            <LeaderboardRow key={i} rank={i + 1} row={row} scope={scope} />
          ))}
        </div>
      )}

      {/* Footer hint */}
      {scope === 'global' && rows.length > 0 && (
        <p className="text-[11px] text-text-muted text-center mt-6" data-testid="leaderboard-footer">
          <Badge variant="outline" className="mr-1">info</Badge>
          {t('tajwid_leaderboard.footer_global_hint')}
        </p>
      )}
    </div>
  );
};

export default TajwidLeaderboard;
